import React, { useState, useEffect, useCallback, useRef } from 'react';
import Editor, { DiffEditor } from '@monaco-editor/react';
import LivePreview from './LivePreview';
import { useTheme } from '../hooks/useTheme';
import { createFileOrFolder, renameFileOrFolder, deleteFileOrFolder, updateFileContent } from '../services/api';

const LANG_MAP = {
  py: 'python', js: 'javascript', ts: 'typescript', tsx: 'typescript',
  jsx: 'javascript', json: 'json', md: 'markdown', css: 'css', html: 'html',
  go: 'go', java: 'java', cs: 'csharp', yaml: 'yaml', yml: 'yaml',
  rb: 'ruby', rs: 'rust', sh: 'shell', sql: 'sql', xml: 'xml',
  toml: 'ini', env: 'ini', dockerfile: 'dockerfile',
};

const FILE_ICONS = {
  py: '🐍', js: '⚡', ts: '🔷', tsx: '⚛️', jsx: '⚛️', json: '{}',
  css: '🎨', html: '🌐', md: '📝', yml: '⚙️', yaml: '⚙️', go: '🔵',
  java: '☕', cs: '🟣', rb: '💎', rs: '🦀', sh: '🖥️', sql: '🗃️',
};

export default function CodeEditor({ files, jobId, diffFiles, minimal = false, onFilesChanged, history }) {
  const { mode } = useTheme();
  const monacoTheme = mode === 'light' ? 'light' : 'vs-dark';
  const [activeFile, setActiveFile] = useState(null);
  const [openTabs, setOpenTabs] = useState([]);
  const [viewMode, setViewMode] = useState('code');
  const [showMinimap, setShowMinimap] = useState(false);
  const [wordWrap, setWordWrap] = useState('off');
  const [showDiff, setShowDiff] = useState(false);
  const [diffInline, setDiffInline] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ line: 1, col: 1 });
  const [contextMenu, setContextMenu] = useState(null); // {x, y, path, isFolder}
  const [newItemInput, setNewItemInput] = useState(null); // {parentPath, type: 'file'|'folder'}
  const [renameInput, setRenameInput] = useState(null); // {path, isFolder}
  const [newItemName, setNewItemName] = useState('');
  const [renameName, setRenameName] = useState('');

  const editorRef = useRef(null);
  // Track content before edit for undo
  const editSnapshotRef = useRef(null);

  // Keyboard shortcuts: Ctrl/Cmd+Z and Ctrl/Cmd+Shift+Z
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      if (e.shiftKey && e.key === 'Z') {
        e.preventDefault();
        handleRedo();
      } else if (!e.shiftKey && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const handleUndo = useCallback(async () => {
    if (!history?.canUndo) return;
    const entry = history.popUndo();
    if (!entry) return;
    try {
      if (entry.type === 'file_edit') {
        await updateFileContent(jobId, entry.path, entry.prevContent);
      } else if (entry.type === 'file_create') {
        await deleteFileOrFolder(jobId, entry.path, false);
        if (activeFile === entry.path) setActiveFile(null);
        setOpenTabs(prev => prev.filter(t => t !== entry.path));
      } else if (entry.type === 'file_delete') {
        await createFileOrFolder(jobId, entry.path, entry.content, false);
      } else if (entry.type === 'file_rename') {
        await renameFileOrFolder(jobId, entry.newPath, entry.oldPath);
        setOpenTabs(prev => prev.map(t => t === entry.newPath ? entry.oldPath : t));
        if (activeFile === entry.newPath) setActiveFile(entry.oldPath);
      } else if (entry.type === 'ai_generation') {
        for (const path of Object.keys(entry.files)) {
          await deleteFileOrFolder(jobId, path, false);
        }
        setOpenTabs(prev => prev.filter(t => !entry.files[t]));
        if (entry.files[activeFile]) setActiveFile(null);
      }
      if (onFilesChanged) onFilesChanged();
    } catch (err) {
      console.error('Undo failed', err);
    }
  }, [history, jobId, activeFile, onFilesChanged]);

  const handleRedo = useCallback(async () => {
    if (!history?.canRedo) return;
    const entry = history.popRedo();
    if (!entry) return;
    try {
      if (entry.type === 'file_edit') {
        await updateFileContent(jobId, entry.path, entry.newContent);
      } else if (entry.type === 'file_create') {
        await createFileOrFolder(jobId, entry.path, entry.content, false);
      } else if (entry.type === 'file_delete') {
        await deleteFileOrFolder(jobId, entry.path, false);
        setOpenTabs(prev => prev.filter(t => t !== entry.path));
        if (activeFile === entry.path) setActiveFile(null);
      } else if (entry.type === 'file_rename') {
        await renameFileOrFolder(jobId, entry.oldPath, entry.newPath);
        setOpenTabs(prev => prev.map(t => t === entry.oldPath ? entry.newPath : t));
        if (activeFile === entry.oldPath) setActiveFile(entry.newPath);
      } else if (entry.type === 'ai_generation') {
        for (const [path, content] of Object.entries(entry.files)) {
          await createFileOrFolder(jobId, path, content, false);
        }
      }
      if (onFilesChanged) onFilesChanged();
    } catch (err) {
      console.error('Redo failed', err);
    }
  }, [history, jobId, activeFile, onFilesChanged]);

  useEffect(() => {
    if (files && Object.keys(files).length > 0 && !activeFile) {
      const first = Object.keys(files)[0];
      setActiveFile(first);
      setOpenTabs([first]);
    }
  }, [files, activeFile]);

  const openFile = useCallback((path) => {
    setActiveFile(path);
    setOpenTabs(prev => prev.includes(path) ? prev : [...prev, path]);
    setViewMode('code');
    setShowDiff(false);
  }, []);

  const closeTab = useCallback((path, e) => {
    e.stopPropagation();
    setOpenTabs(prev => {
      const newTabs = prev.filter(t => t !== path);
      if (activeFile === path) setActiveFile(newTabs[0] || null);
      return newTabs;
    });
  }, [activeFile]);

  const getExt = (path) => {
    const name = path.split('/').pop().toLowerCase();
    if (name === 'dockerfile') return 'dockerfile';
    return name.split('.').pop();
  };

  const getLanguage = (path) => LANG_MAP[getExt(path)] || 'plaintext';
  const getIcon = (path) => FILE_ICONS[getExt(path)] || '📄';

  const canPreview = () =>
    files && Object.keys(files).some(k => k.match(/\.(js|jsx|tsx|ts|html|css)$/));

  const hasDiff = (path) => diffFiles && diffFiles[path];

  const handleEditorMount = (editor) => {
    editorRef.current = editor;
    editor.onDidChangeCursorPosition((e) => {
      setCursorPosition({ line: e.position.lineNumber, col: e.position.column });
    });
  };

  // Close context menu on click anywhere
  useEffect(() => {
    const close = () => setContextMenu(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const handleContextMenu = useCallback((e, path, isFolder) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, path, isFolder });
  }, []);

  const handleNewItem = useCallback(async (type) => {
    if (!newItemName.trim()) { setNewItemInput(null); return; }
    const parentPath = newItemInput?.parentPath || '';
    const fullPath = parentPath ? `${parentPath}/${newItemName.trim()}` : newItemName.trim();
    try {
      await createFileOrFolder(jobId, fullPath, '', type === 'folder');
      if (type !== 'folder' && history) {
        history.push({ type: 'file_create', path: fullPath, content: '' });
      }
      if (onFilesChanged) onFilesChanged();
    } catch (err) {
      alert(err.message);
    }
    setNewItemInput(null);
    setNewItemName('');
  }, [newItemName, newItemInput, jobId, onFilesChanged, history]);

  const handleRename = useCallback(async () => {
    if (!renameName.trim() || !renameInput) { setRenameInput(null); return; }
    const oldPath = renameInput.path;
    const parts = oldPath.split('/');
    parts[parts.length - 1] = renameName.trim();
    const newPath = parts.join('/');
    if (oldPath === newPath) { setRenameInput(null); return; }
    try {
      await renameFileOrFolder(jobId, oldPath, newPath);
      if (!renameInput.isFolder && history) {
        history.push({ type: 'file_rename', oldPath, newPath });
      }
      if (onFilesChanged) onFilesChanged();
    } catch (err) {
      alert(err.message);
    }
    setRenameInput(null);
    setRenameName('');
  }, [renameName, renameInput, jobId, onFilesChanged, history]);

  const handleDelete = useCallback(async (path, isFolder) => {
    const label = isFolder ? 'folder and all its contents' : 'file';
    if (!window.confirm(`Delete this ${label}?\n${path}`)) return;
    try {
      const deletedContent = !isFolder ? (files[path] || '') : null;
      await deleteFileOrFolder(jobId, path, isFolder);
      if (!isFolder && history) {
        history.push({ type: 'file_delete', path, content: deletedContent });
      }
      if (activeFile === path || (isFolder && activeFile?.startsWith(path + '/'))) {
        setActiveFile(null);
        setOpenTabs(prev => prev.filter(t => t !== path && !(isFolder && t.startsWith(path + '/'))));
      }
      if (onFilesChanged) onFilesChanged();
    } catch (err) {
      alert(err.message);
    }
  }, [jobId, activeFile, onFilesChanged, files, history]);

  if (!files || Object.keys(files).length === 0) return null;

  const tree = buildTree(Object.keys(files));
  const fileCount = Object.keys(files).length;
  const activeContent = activeFile ? files[activeFile] || '' : '';
  const lineCount = activeContent.split('\n').length;

  return (
    <div style={s.container}>
      {/* Top Bar */}
      <div style={s.topBar}>
        <div style={s.topLeft}>
          <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} style={s.iconBtn} title="Toggle sidebar">
            {sidebarCollapsed ? '☰' : '✕'}
          </button>
          <span style={s.topTitle}>{minimal ? '📂 Project Files' : '💻 Code Editor'}</span>
          <span style={s.badge}>{fileCount} files</span>
        </div>
        <div style={s.topRight}>
          {/* Undo / Redo */}
          {history && (
            <>
              <button
                onClick={handleUndo}
                disabled={!history.canUndo}
                style={{ ...s.settingBtn, ...(history.canUndo ? {} : s.settingDisabled) }}
                title="Undo (Ctrl+Z)"
              >
                ↩ Undo
              </button>
              <button
                onClick={handleRedo}
                disabled={!history.canRedo}
                style={{ ...s.settingBtn, ...(history.canRedo ? {} : s.settingDisabled) }}
                title="Redo (Ctrl+Shift+Z)"
              >
                ↪ Redo
              </button>
            </>
          )}
          {/* Editor Settings */}
          <button onClick={() => setShowMinimap(!showMinimap)} style={{ ...s.settingBtn, ...(showMinimap ? s.settingActive : {}) }} title="Toggle minimap">
            ▪ Map
          </button>
          <button onClick={() => setWordWrap(wordWrap === 'off' ? 'on' : 'off')} style={{ ...s.settingBtn, ...(wordWrap === 'on' ? s.settingActive : {}) }} title="Toggle word wrap">
            ↩ Wrap
          </button>

          {/* View Toggle */}
          <div style={s.viewToggle}>
            <button onClick={() => { setViewMode('code'); setShowDiff(false); }} style={{ ...s.toggleBtn, ...(viewMode === 'code' && !showDiff ? s.toggleActive : {}) }}>
              {'<>'} Code
            </button>
            {hasDiff(activeFile) && (
              <button onClick={() => setShowDiff(!showDiff)} style={{ ...s.toggleBtn, ...(showDiff ? s.toggleActive : {}) }}>
                ± Diff
              </button>
            )}
            {!minimal && canPreview() && (
              <button onClick={() => { setViewMode('preview'); setShowDiff(false); }} style={{ ...s.toggleBtn, ...(viewMode === 'preview' ? s.toggleActive : {}) }}>
                👁 Preview
              </button>
            )}
          </div>
        </div>
      </div>


      {/* Main Body */}
      <div style={s.body}>
        {/* Sidebar - File Tree */}
        {!sidebarCollapsed && (
          <div style={s.sidebar}>
            <div style={s.sidebarHeader}>
              <span style={s.sidebarTitle}>EXPLORER</span>
              <div style={s.sidebarActions}>
                <button title="New File" style={s.sidebarActionBtn} onClick={() => { setNewItemInput({ parentPath: '', type: 'file' }); setNewItemName(''); }}>+📄</button>
                <button title="New Folder" style={s.sidebarActionBtn} onClick={() => { setNewItemInput({ parentPath: '', type: 'folder' }); setNewItemName(''); }}>+📁</button>
              </div>
            </div>
            {newItemInput && newItemInput.parentPath === '' && (
              <div style={s.inlineInput}>
                <span style={{ fontSize: 12 }}>{newItemInput.type === 'folder' ? '📁' : '📄'}</span>
                <input
                  autoFocus
                  value={newItemName}
                  onChange={e => setNewItemName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleNewItem(newItemInput.type); if (e.key === 'Escape') { setNewItemInput(null); setNewItemName(''); } }}
                  onBlur={() => handleNewItem(newItemInput.type)}
                  style={s.inlineInputField}
                  placeholder={newItemInput.type === 'folder' ? 'folder name' : 'filename.ext'}
                />
              </div>
            )}
            <TreeNode
              node={tree} onSelect={openFile} activeFile={activeFile} getIcon={getIcon} depth={0}
              onContextMenu={handleContextMenu}
              renameInput={renameInput} renameName={renameName}
              onRenameChange={setRenameName} onRenameSubmit={handleRename} onRenameCancel={() => { setRenameInput(null); setRenameName(''); }}
              newItemInput={newItemInput} newItemName={newItemName}
              onNewItemChange={setNewItemName} onNewItemSubmit={handleNewItem} onNewItemCancel={() => { setNewItemInput(null); setNewItemName(''); }}
            />
          </div>
        )}

        {/* Context Menu */}
        {contextMenu && (
          <div style={{ ...s.ctxMenu, top: contextMenu.y, left: contextMenu.x }} onClick={e => e.stopPropagation()}>
            {contextMenu.isFolder && (
              <>
                <div style={s.ctxItem} onClick={() => { setNewItemInput({ parentPath: contextMenu.path, type: 'file' }); setNewItemName(''); setContextMenu(null); }}>📄 New File</div>
                <div style={s.ctxItem} onClick={() => { setNewItemInput({ parentPath: contextMenu.path, type: 'folder' }); setNewItemName(''); setContextMenu(null); }}>📁 New Folder</div>
                <div style={s.ctxSep} />
              </>
            )}
            <div style={s.ctxItem} onClick={() => { setRenameInput({ path: contextMenu.path, isFolder: contextMenu.isFolder }); setRenameName(contextMenu.path.split('/').pop()); setContextMenu(null); }}>✏️ Rename</div>
            <div style={{ ...s.ctxItem, color: '#ef4444' }} onClick={() => { handleDelete(contextMenu.path, contextMenu.isFolder); setContextMenu(null); }}>🗑️ Delete</div>
          </div>
        )}

        {/* Editor Area */}
        <div style={s.editorArea}>
          {/* Tabs */}
          <div style={s.tabBar}>
            {openTabs.map(tab => (
              <div key={tab} onClick={() => { setActiveFile(tab); setViewMode('code'); setShowDiff(false); }}
                style={{ ...s.tab, ...(tab === activeFile ? s.tabActive : {}) }}>
                <span style={{ fontSize: 12 }}>{getIcon(tab)}</span>
                <span>{tab.split('/').pop()}</span>
                {hasDiff(tab) && <span style={s.diffDot}>●</span>}
                <span onClick={(e) => closeTab(tab, e)} style={s.tabClose}>×</span>
              </div>
            ))}
          </div>

          {/* Breadcrumb */}
          {activeFile && (
            <div style={s.breadcrumb}>
              {activeFile.split('/').map((part, i, arr) => (
                <span key={i}>
                  <span style={{ color: i === arr.length - 1 ? 'var(--text-primary)' : 'var(--text-muted)' }}>{part}</span>
                  {i < arr.length - 1 && <span style={{ color: 'var(--text-muted)', margin: '0 4px' }}>/</span>}
                </span>
              ))}
            </div>
          )}

          {/* Content Area */}
          <div style={s.content}>
            {viewMode === 'preview' && canPreview() ? (
              <LivePreview files={files} />
            ) : showDiff && activeFile && hasDiff(activeFile) ? (
              /* Diff View */
              <div style={{ height: '100%', position: 'relative' }}>
                <div style={s.diffToolbar}>
                  <span style={s.diffLabel}>Showing changes from Debugger Agent</span>
                  <button onClick={() => setDiffInline(!diffInline)} style={s.diffToggle}>
                    {diffInline ? '⇔ Side by Side' : '⇕ Inline'}
                  </button>
                </div>
                <DiffEditor
                  height="calc(100% - 32px)"
                  language={getLanguage(activeFile)}
                  original={diffFiles[activeFile].original}
                  modified={diffFiles[activeFile].modified || files[activeFile]}
                  theme={monacoTheme}
                  options={{
                    readOnly: true,
                    renderSideBySide: !diffInline,
                    fontSize: 14,
                    fontFamily: "'JetBrains Mono', 'SF Mono', 'Fira Code', monospace",
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                  }}
                />
              </div>
            ) : activeFile && files[activeFile] ? (
              /* Monaco Editor */
              <Editor
                height="100%"
                language={getLanguage(activeFile)}
                value={files[activeFile]}
                theme={monacoTheme}
                onMount={handleEditorMount}
                onChange={(newValue) => {
                  if (!history || newValue === undefined) return;
                  if (editSnapshotRef.current === null) {
                    editSnapshotRef.current = files[activeFile] || '';
                  }
                  clearTimeout(editSnapshotRef._timer);
                  editSnapshotRef._timer = setTimeout(async () => {
                    const prevContent = editSnapshotRef.current;
                    editSnapshotRef.current = null;
                    if (prevContent === newValue) return;
                    try {
                      await updateFileContent(jobId, activeFile, newValue);
                      history.push({ type: 'file_edit', path: activeFile, prevContent, newContent: newValue });
                      if (onFilesChanged) onFilesChanged();
                    } catch (err) {
                      console.error('Save failed', err);
                    }
                  }, 1000);
                }}
                options={{
                  readOnly: false,
                  fontSize: 14,
                  fontFamily: "'JetBrains Mono', 'SF Mono', 'Fira Code', monospace",
                  minimap: { enabled: showMinimap },
                  wordWrap: wordWrap,
                  lineNumbers: 'on',
                  bracketPairColorization: { enabled: true },
                  scrollBeyondLastLine: false,
                  smoothScrolling: true,
                  cursorBlinking: 'smooth',
                  renderLineHighlight: 'line',
                  matchBrackets: 'always',
                  automaticLayout: true,
                  padding: { top: 8 },
                }}
              />
            ) : (
              <div style={s.emptyState}>
                <span style={{ fontSize: 48, opacity: 0.3 }}>📂</span>
                <p style={{ color: 'var(--text-muted)', marginTop: 12 }}>Select a file from the explorer</p>
              </div>
            )}
          </div>

          {/* Status Bar */}
          <div style={s.statusBar}>
            <span style={s.statusItem}>{activeFile ? getLanguage(activeFile) : 'No file'}</span>
            <span style={s.statusItem}>Ln {cursorPosition.line}, Col {cursorPosition.col}</span>
            <span style={s.statusItem}>{lineCount} lines</span>
            <span style={s.statusItem}>UTF-8</span>
            <span style={{ ...s.statusItem, marginLeft: 'auto' }}>AI Code Agent v0.2.0</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── File Tree Builder ──────────────────────────────────────────────────────

function buildTree(paths) {
  const root = { name: '', children: {}, files: [] };

  // Normalize paths: bare src/ files that are frontend code → frontend/src/
  const hasFrontendFolder = paths.some(p => p.startsWith('frontend/'));
  const hasBackendFolder = paths.some(p => p.startsWith('backend/'));

  const normalizedPaths = paths.map(p => {
    // If project has frontend/ and backend/ folders, move bare src/ files into frontend/
    if (hasFrontendFolder && hasBackendFolder && p.startsWith('src/')) {
      return 'frontend/' + p;
    }
    return p;
  });

  normalizedPaths.forEach((p, i) => {
    const originalPath = paths[i];
    const parts = p.split('/');
    let node = root;
    parts.forEach((part, idx) => {
      if (idx === parts.length - 1) node.files.push(originalPath);
      else {
        if (!node.children[part]) node.children[part] = { name: part, children: {}, files: [] };
        node = node.children[part];
      }
    });
  });
  return root;
}

function TreeNode({ node, onSelect, activeFile, getIcon, depth, onContextMenu, renameInput, renameName, onRenameChange, onRenameSubmit, onRenameCancel, newItemInput, newItemName, onNewItemChange, onNewItemSubmit, onNewItemCancel }) {
  const folders = Object.entries(node.children).sort(([a], [b]) => a.localeCompare(b));
  const filesSorted = [...node.files].sort((a, b) => a.split('/').pop().localeCompare(b.split('/').pop()));

  return (
    <>
      {folders.map(([name, child]) => (
        <FolderNode key={name} name={name} node={child} onSelect={onSelect} activeFile={activeFile} getIcon={getIcon} depth={depth}
          onContextMenu={onContextMenu}
          renameInput={renameInput} renameName={renameName}
          onRenameChange={onRenameChange} onRenameSubmit={onRenameSubmit} onRenameCancel={onRenameCancel}
          newItemInput={newItemInput} newItemName={newItemName}
          onNewItemChange={onNewItemChange} onNewItemSubmit={onNewItemSubmit} onNewItemCancel={onNewItemCancel}
        />
      ))}
      {filesSorted.map(f => {
        const isRenaming = renameInput && renameInput.path === f && !renameInput.isFolder;
        return (
          <div key={f} onClick={() => !isRenaming && onSelect(f)}
            onContextMenu={e => onContextMenu && onContextMenu(e, f, false)}
            style={{ ...s.treeItem, paddingLeft: 8 + depth * 14, ...(f === activeFile ? s.treeItemActive : {}) }}>
            <span style={{ width: 14 }} />
            <span style={{ fontSize: 13 }}>{getIcon(f)}</span>
            {isRenaming ? (
              <input
                autoFocus
                value={renameName}
                onChange={e => onRenameChange(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') onRenameSubmit(); if (e.key === 'Escape') onRenameCancel(); }}
                onBlur={onRenameSubmit}
                onClick={e => e.stopPropagation()}
                style={s.inlineInputField}
              />
            ) : (
              <span style={s.treeName}>{f.split('/').pop()}</span>
            )}
          </div>
        );
      })}
    </>
  );
}

function FolderNode({ name, node, onSelect, activeFile, getIcon, depth, onContextMenu, renameInput, renameName, onRenameChange, onRenameSubmit, onRenameCancel, newItemInput, newItemName, onNewItemChange, onNewItemSubmit, onNewItemCancel }) {
  const [expanded, setExpanded] = useState(depth < 2);

  // Derive the full folder path from the node's files or children
  const folderPath = (() => {
    if (node.files.length > 0) {
      const f = node.files[0];
      const parts = f.split('/');
      parts.pop();
      return parts.join('/');
    }
    const childKeys = Object.keys(node.children);
    if (childKeys.length > 0) {
      // Recurse into first child to find a file
      const findPath = (n, prefix) => {
        if (n.files.length > 0) { const p = n.files[0].split('/'); p.pop(); return p.join('/'); }
        for (const [k, v] of Object.entries(n.children)) return findPath(v, prefix + '/' + k);
        return prefix;
      };
      return findPath(node.children[childKeys[0]], name + '/' + childKeys[0]);
    }
    return name;
  })();

  // Check if this folder's path ends with the folder name (for rename detection)
  const isRenaming = renameInput && renameInput.isFolder && renameInput.path === folderPath;
  const showNewItem = newItemInput && newItemInput.parentPath === folderPath;

  return (
    <div>
      <div onClick={() => setExpanded(!expanded)}
        onContextMenu={e => onContextMenu && onContextMenu(e, folderPath, true)}
        style={{ ...s.treeItem, paddingLeft: 8 + depth * 14 }}>
        <span style={s.treeChevron}>{expanded ? '▾' : '▸'}</span>
        <span style={{ fontSize: 13 }}>{expanded ? '📂' : '📁'}</span>
        {isRenaming ? (
          <input
            autoFocus
            value={renameName}
            onChange={e => onRenameChange(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') onRenameSubmit(); if (e.key === 'Escape') onRenameCancel(); }}
            onBlur={onRenameSubmit}
            onClick={e => e.stopPropagation()}
            style={s.inlineInputField}
          />
        ) : (
          <span style={s.treeName}>{name}</span>
        )}
      </div>
      {expanded && (
        <>
          {showNewItem && (
            <div style={{ ...s.inlineInput, paddingLeft: 8 + (depth + 1) * 14 }}>
              <span style={{ fontSize: 12 }}>{newItemInput.type === 'folder' ? '📁' : '📄'}</span>
              <input
                autoFocus
                value={newItemName}
                onChange={e => onNewItemChange(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') onNewItemSubmit(newItemInput.type); if (e.key === 'Escape') onNewItemCancel(); }}
                onBlur={() => onNewItemSubmit(newItemInput.type)}
                style={s.inlineInputField}
                placeholder={newItemInput.type === 'folder' ? 'folder name' : 'filename.ext'}
              />
            </div>
          )}
          <TreeNode node={node} onSelect={onSelect} activeFile={activeFile} getIcon={getIcon} depth={depth + 1}
            onContextMenu={onContextMenu}
            renameInput={renameInput} renameName={renameName}
            onRenameChange={onRenameChange} onRenameSubmit={onRenameSubmit} onRenameCancel={onRenameCancel}
            newItemInput={newItemInput} newItemName={newItemName}
            onNewItemChange={onNewItemChange} onNewItemSubmit={onNewItemSubmit} onNewItemCancel={onNewItemCancel}
          />
        </>
      )}
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const s = {
  container: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' },

  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' },
  topLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  topRight: { display: 'flex', alignItems: 'center', gap: 6 },
  topTitle: { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' },
  badge: { fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--border)', padding: '2px 8px', borderRadius: 10, fontFamily: "'JetBrains Mono', monospace" },
  iconBtn: { background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 14, cursor: 'pointer', padding: '4px 8px', borderRadius: 4 },

  settingBtn: { padding: '3px 8px', border: '1px solid var(--border)', borderRadius: 4, background: 'transparent', color: 'var(--text-muted)', fontSize: 10, fontWeight: 600, cursor: 'pointer' },
  settingActive: { background: 'rgba(99,102,241,0.15)', color: 'var(--accent-light)', borderColor: 'rgba(99,102,241,0.3)' },
  settingDisabled: { opacity: 0.35, cursor: 'not-allowed' },

  viewToggle: { display: 'flex', background: 'var(--bg-card)', borderRadius: 6, padding: 2 },
  toggleBtn: { padding: '4px 10px', border: 'none', borderRadius: 4, background: 'transparent', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' },
  toggleActive: { background: 'rgba(99,102,241,0.2)', color: 'var(--accent-light)' },

  actionBtn: { padding: '4px 10px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' },
  gitBtn: { borderColor: 'rgba(99,102,241,0.3)', color: 'var(--accent-light)' },

  banner: { padding: '6px 16px', fontSize: 12, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)' },

  body: { display: 'flex', height: 560 },

  sidebar: { width: 220, borderRight: '1px solid var(--border)', overflowY: 'auto', background: 'var(--bg-primary)' },
  sidebarHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px 8px 16px' },
  sidebarTitle: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.1em' },
  sidebarActions: { display: 'flex', gap: 2 },
  sidebarActionBtn: { background: 'none', border: 'none', fontSize: 12, cursor: 'pointer', padding: '2px 4px', borderRadius: 4, color: 'var(--text-muted)', lineHeight: 1 },
  inlineInput: { display: 'flex', alignItems: 'center', gap: 6, padding: '2px 8px', margin: '0 4px' },
  inlineInputField: { flex: 1, background: 'var(--bg-input, var(--bg-card))', border: '1px solid var(--accent-light, #6366f1)', borderRadius: 3, padding: '2px 6px', fontSize: 12, color: 'var(--text-primary)', outline: 'none', minWidth: 0 },
  ctxMenu: { position: 'fixed', zIndex: 9999, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 0', boxShadow: '0 8px 24px rgba(0,0,0,0.4)', minWidth: 150 },
  ctxItem: { padding: '6px 14px', fontSize: 12, cursor: 'pointer', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 },
  ctxSep: { height: 1, background: 'var(--border)', margin: '4px 0' },
  treeItem: { display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 12, color: 'var(--text-secondary)', borderRadius: 4, margin: '0 4px' },
  treeItemActive: { background: 'rgba(99,102,241,0.15)', color: 'var(--text-primary)' },
  treeChevron: { fontSize: 10, color: 'var(--text-muted)', width: 14, textAlign: 'center' },
  treeName: { fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },

  editorArea: { flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg-card)', minWidth: 0 },

  tabBar: { display: 'flex', background: 'var(--bg-primary)', borderBottom: '1px solid var(--border)', overflowX: 'auto', minHeight: 35 },
  tab: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)', borderRight: '1px solid var(--bg-card)', whiteSpace: 'nowrap' },
  tabActive: { background: 'var(--bg-card)', color: 'var(--text-primary)', borderTop: '2px solid #6366f1', marginTop: -2 },
  tabClose: { fontSize: 14, cursor: 'pointer', opacity: 0.4, marginLeft: 4, lineHeight: 1 },
  diffDot: { fontSize: 8, color: '#f59e0b', marginLeft: 2 },

  breadcrumb: { padding: '4px 16px', fontSize: 11, background: 'var(--code-breadcrumb)', borderBottom: '1px solid var(--border)' },

  content: { flex: 1, overflow: 'hidden' },

  diffToolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 12px', background: 'rgba(245,158,11,0.08)', borderBottom: '1px solid rgba(245,158,11,0.2)' },
  diffLabel: { fontSize: 11, color: '#f59e0b', fontWeight: 600 },
  diffToggle: { padding: '2px 8px', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 4, background: 'transparent', color: '#f59e0b', fontSize: 10, fontWeight: 600, cursor: 'pointer' },

  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' },

  statusBar: { display: 'flex', alignItems: 'center', gap: 16, padding: '3px 16px', fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-primary)', borderTop: '1px solid var(--border)' },
  statusItem: { padding: '0 4px' },
};
